import cx from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import React, { FunctionComponent, useMemo } from 'react';

import useLinkProps from '../../../hooks/use-link-props';
import EcoRepairScoreSchema from './seo';
import Breadcrumbs from '../../modules/v2/breadcrumbs';
import Trans from 'next-translate/Trans';
import BPImage from '../../ui/v2/image';
import Trustpilot from '../../modules/trustpilot';
import Link from 'next/link';
import FeaturedLink from '../../ui/links/featured';
import useIsMobile from '../../../hooks/use-is-mobile';
import dynamic from 'next/dynamic';
const TrustBox = dynamic(() => import('../../modules/trustpilot/new'), { ssr: false });

const whatIsEcoRepairModulePoints = [
  {
    key: "eco-repair:whatIsEcoRepairModule.points.eco_repair_A",
    color: "bg-eco-repair-A",
    title: "A"
  },
  {
    key: "eco-repair:whatIsEcoRepairModule.points.eco_repair_B",
    color: "bg-eco-repair-B",
    title: "B",
  },
  {
    key: "eco-repair:whatIsEcoRepairModule.points.eco_repair_C",
    color: "bg-eco-repair-C",
    title: "C",
  },
  {
    key: "eco-repair:whatIsEcoRepairModule.points.eco_repair_D",
    color: "bg-eco-repair-D",
    title: "D",
  },
  {
    key: "eco-repair:whatIsEcoRepairModule.points.eco_repair_E",
    color: "bg-eco-repair-E",
    title: "E",
  }
]

const sustentabilitySectionPoints = [
  {
    key: "eco-repair:sustentabilitySection.points.eco_repair_sustantibility_trips",
    icon: "leaf_green",
  },
  {
    key: "eco-repair:sustentabilitySection.points.eco_repair_sustantibility_savings",
    icon: "save_money_green",
  },
  {
    key: "eco-repair:sustentabilitySection.points.eco_repair_sustantibility_reductions",
    icon: "door_green",
  }
]

const EcoRepairScorePage: FunctionComponent<TPageContacts> = ({ }) => {
  const { t } = useTranslation();
  const getLink = useLinkProps();
  const isMobile = useIsMobile()

  return (
    <main>
      <EcoRepairScoreSchema breadcrumbs={[]} />
      <Breadcrumbs items={[]} currentPage={t('eco-repair:pageTitle')} />
      <section
        className={cx(
          'relative ',
          'text-white',
          'py-6 px-6 lg:py-24',
        )}
      >
        <BPImage
          src={`${process.env.PUBLIC_PREFIX}/img/${isMobile ? 'banner_ecorepairscore_mobile.jpg' : 'banner_ecorepairscore.jpg'}`}
          key={isMobile ? 'ecoMobile' : 'ecodesktop'}
          alt="image"
          fill={true}
          sizes="100%"
          className="object-cover object-right-top"
          quality={100}
        />
        <div
          className={cx(
            'lg:container mx-auto w-full',
            'relative',
            'z-10',
            'flex flex-row gap-3 justify-between items-start lg:gap-8 lg:px-4'
          )}
        >
          <div className="w-full lg:self-center flex flex-col items-left gap-6 lg:gap-4">
            <h1
              className={cx(
                'text-[32px] leading-[38px] lg:text-[52px] lg:leading-[62px] font-bold',
                'text-left'
              )}
            >
              {t('eco-repair:pageTitle')}
            </h1>
            <div className='flex lg:flex-row justify-between gap-4'>
              <h2 className={cx(
                'text-xl lg:text-[32px] lg:leading-[62px] font-bold',
                'text-left max-lg:w-2/3'
              )}>{t('eco-repair:pageDescription')}</h2>

              <div className='lg:hidden relative max-lg:w-1/3 flex flex-row justify-end items-center max-h-[80px]'>
                <BPImage
                  src={`${process.env.PUBLIC_PREFIX}/svg/eco-repair/main-image-large.svg`}
                  alt="image"
                  width={67}
                  height={80}
                  sizes="100%"
                />
              </div>
            </div>
          </div>
          {!isMobile && <div className='max-lg:hidden'>
            <BPImage
              src={`${process.env.PUBLIC_PREFIX}/svg/eco-repair/main-image-large.svg`}
              alt="image"
              width={160}
              height={191}
              sizes="100%"
            />
          </div>}
        </div>
      </section>
      <section className='max-lg:px-4 container mx-auto py-8 lg:py-12 flex flex-col gap-12'>
        <div className='text-center'>
          <h3 className={cx('pb-[14px] text-[28px] leading-[34px] lg:text-[32px] lg:leading-[62px] font-bold text-center')}>{t('eco-repair:whatIsEcoRepairModule.title')}</h3>
          <span className={cx('text-base lg:text-[18px] lg:leading-[23px] font-bold text-center')}>{t('eco-repair:whatIsEcoRepairModule.description')}</span>
        </div>
        <div className={cx('flex flex-wrap justify-center gap-8 lg:gap-12')}>
          {whatIsEcoRepairModulePoints.map((el, index) => {
            return <div key={index} className={cx('w-full lg:w-[calc(33.333%-96px)] grid grid-cols-[44px_1fr] gap-4')}>
              <div className={cx(el.color, 'h-fit text-base font-bold py-[10px] px-[6px] text-center text-white')}>{el.title}</div>
              <div>{t(el.key)}</div>
            </div>;
          })}
        </div>
      </section>
      <section className='bg-neutral-10 w-full'>
        <div className='max-lg:px-4 container mx-auto py-8 lg:py-12 flex flex-col gap-12'>
          <div className='flex flex-col lg:flex-row relative justify-between gap-12'>
            <div className='lg:hidden flex flex-row relative justify-between'>
              <div className='w-2/3'>
                <h3 className={cx('pb-[14px] text-[28px] leading-[34px] lg:text-[32px] lg:leading-[62px] font-bold text-left lg:pb-8')}>{t('eco-repair:ecoRepairMethodModule.title')}</h3>
              </div>
              <div className='lg:hidden w-1/3 relative'>
                <BPImage
                  src={'/img/leaf_co2.jpeg'}
                  layout="fill" objectFit="contain"
                  objectPosition="right"
                  className={cx(
                    'bg-blend-multiply w-full '
                  )}
                  alt={'co2'}
                />
              </div>
            </div>
            <div className='lg:hidden'>
              <span className={cx('text-base lg:text-[18px] lg:leading-[23px] text-center')}>{t('eco-repair:ecoRepairMethodModule.description')}</span>
              <p className={cx('pt-4 text-base lg:text-[18px] lg:leading-[23px] text-left lg:pt-6')}>
                <Trans
                  i18nKey="eco-repair:ecoRepairMethodModule.moreInfo"
                  components={{
                    contactLink: <Link href={"mailto:b2b@b-parts.com"} passHref legacyBehavior>
                      <FeaturedLink variant='blackBold' className="leading-7">
                        {t('help:contact_link')}
                      </FeaturedLink>
                    </Link>
                  }}
                />
              </p>
            </div>
            <div className='max-lg:hidden w-5/6'>
              <h3 className={cx('pb-[14px] text-[28px] leading-[34px] lg:text-[32px] lg:leading-[62px] font-bold text-left lg:pb-8')}>{t('eco-repair:ecoRepairMethodModule.title')}</h3>
              <span className={cx('text-base lg:text-[18px] lg:leading-[23px] text-center')}>{t('eco-repair:ecoRepairMethodModule.description')}</span>
              <p className={cx('pt-4 text-base lg:text-[18px] lg:leading-[23px] text-left lg:pt-6')}>
                <Trans
                  i18nKey="eco-repair:ecoRepairMethodModule.moreInfo"
                  components={{
                    contactLink: <Link href={"mailto:b2b@b-parts.com"} passHref legacyBehavior>
                      <FeaturedLink variant='blackBold' className="leading-7">
                        {t('help:contact_link')}
                      </FeaturedLink>
                    </Link>
                  }}
                />
              </p>
            </div>
            <div className='max-lg:hidden lg:block w-1/6 relative'>
              <BPImage
                src={`${process.env.PUBLIC_PREFIX}/img/leaf_co2.jpeg`}
                layout="fill" objectFit="contain"
                objectPosition="right"
                className={cx(
                  'bg-blend-multiply w-full '
                )}
                alt={'co2'}
              />
            </div>
          </div>
        </div>
      </section>
      <section className='w-full'>
        <div className='max-lg:px-4 container mx-auto py-8 lg:py-12 flex flex-col gap-12'>
          <div className='flex flew-row justify-between gap-12'>
            <div className='w-full lg:w-4/5 flex flex-col gap-8 items-start justify-start'>
              <div className='flex flex-col gap-8 items-start justify-start'>
                <h3 className={cx('text-[28px] leading-[34px] lg:text-[32px] lg:leading-[62px] font-bold text-left')}>
                  <Trans
                    i18nKey="eco-repair:sustentabilitySection.title"
                    components={{
                      b: <b></b>
                    }} />
                </h3>
                <span className={cx('text-base lg:text-[18px] lg:leading-[23px] text-left')}>
                  <Trans
                    i18nKey="eco-repair:sustentabilitySection.description"
                    components={{
                      b: <b></b>
                    }} /></span>
              </div>
              <div className='lg:hidden w-full relative h-[338px] flex justify-center'>
                <BPImage
                  src={`${process.env.PUBLIC_PREFIX}/img/porto_london.jpeg`}
                  width={259}
                  height={338}
                  sizes="100%"
                  alt={'co2'}
                />
              </div>
              <div className='grid max-lg:grid-rows-3 lg:grid-cols-3 flex-wrap justify-start gap-8 lg:gap-12 lg:pt-8 lg:pb-8'>
                {sustentabilitySectionPoints.map((el, index) => {
                  return <div key={`sustentability_${index}`} className={cx('w-full  grid grid-cols-[32px_1fr] gap-4')}>
                    <div className={cx('h-fit text-base font-bold text-center text-white')}>
                      <BPImage
                        src={`/svg/eco-repair/${el.icon}.svg`}
                        alt="image"
                        width={32}
                        height={32}
                        sizes="100%"
                      />
                    </div>
                    <div><Trans
                      i18nKey={el.key}
                      components={{
                        b: <b></b>
                      }} /></div>
                  </div>
                })}
              </div>
            </div>
            <div className='max-lg:hidden lg:block lg:w-1/5 relative'>
              <BPImage
                src={`${process.env.PUBLIC_PREFIX}/img/porto_london.jpeg`}
                layout="fill" objectFit="contain"
                objectPosition="right"
                className={cx(
                  'bg-blend-multiply w-full '
                )}
                alt={'co2'}
              />
            </div>
          </div>
        </div>
      </section>
      <div className={cx('py-8 lg:py-6')}>
        <div className={cx('container', 'mx-auto', 'text-center ')}>
          <TrustBox />
        </div>
      </div>
    </main>
  );
};

export default EcoRepairScorePage;
